import styled from "styled-components";
import { theme } from "../../../theme";
import { useRef, useState, type FC } from "react";
import { useCreateInvoiceUploadUnit } from "../../../graphql/hooks/documentHandler/useCreateInvoiceUploadUnit";
import type { InvoiceServiceType } from "../../../models/InvoiceServiceType";
import { Col, Row } from "react-grid-system";
import Subtitle from "../../atoms/typography/Subtitle";
import { HorizontalSpacer, VerticalSpacer } from "../../atoms/Spacers";
import UploadFile from "../UploadFiles";
import OutlinedButton from "../../atoms/buttons/OutlinedButton";
import Body from "../../atoms/typography/Body";
import { FileAdminIcon, FileIconOne } from "../../atoms/icons/FileIcons";
import IconButton from "../../atoms/buttons/IconButton";
import { TrashIcon } from "../../atoms/icons/TrashIcon";
import { SlideOutHeight } from "../../atoms/SlideOutHeight";
import TextField from "../../atoms/inputs/TextField";
import MultilineTextField from "../../atoms/inputs/MultilineTextField";
import Lookup from "../../organisms/Lookup";
import DefaultButton from "../../atoms/buttons/DefaultButton";
import { CrossIcon } from "../../atoms/icons/CrossIcon";
import ActionButton from "../../atoms/buttons/ActionButton";
import { UploadIcon } from "../../atoms/icons/UploadIcon";
import Alert from "../Alert";
import { useTranslation } from "react-i18next";
import Subheading from "../../atoms/typography/Subheading";
import TextButton from "../../atoms/buttons/TextButton";
import ToggleButton from "../../atoms/buttons/ToggleButton";
import Tooltip from "../Tooltip";
import ChevronButton from "../../atoms/buttons/ChevronButtons";
import Checkbox from "../../atoms/inputs/Checkbox";

interface Props {
  onClose: () => void;
}

const FileContainer = styled.div`
  padding: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid ${theme.colors.border};
`;

const IconRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;
`;

const FileRow = styled(Row)`
  cursor: pointer;
  padding: 0px 8px;
  border-radius: 8px;
  &:hover {
    background-color: ${theme.colors.hover};
  }
`;

interface FileObject {
  name: string;
  file: File;
  adminOnly?: boolean;
  recipientEmail?: string;
  deliveryApprovalEmail?: string;
  gln?: string;
  comment?: string;
}

const UploadDocumentModalContent: FC<Props> = ({ onClose }) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState<FileObject[]>([]);
  const [filesOpen, setFilesOpen] = useState<Array<string>>([]);
  const [clientIdentifier, setClientIdentifier] = useState("");
  const [clientName, setClientName] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [disableAutoInterpretation, setDisableAutoInterpretation] =
    useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleUploadFile = async (files: File | File[]) => {
    const fileArray = Array.isArray(files) ? files : [files];
    const fileObjects = fileArray.map((file) => ({
      name: file.name,
      file,
      adminOnly: false,
      recipientEmail: "",
      gln: "",
      comment: "",
    }));
    setFiles(fileObjects);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      handleUploadFile(Array.from(files));
    }
  };

  const onClickRemoveFile = (file: FileObject) => {
    setFiles((prevFiles) => prevFiles.filter((f) => f.name !== file.name));
  };

  const checkFileOpen = (fileName: string) => {
    const fileIsOpen = filesOpen.includes(fileName);
    return fileIsOpen;
  };

  const onClickOpenFile = (fileName: string) => {
    if (checkFileOpen(fileName)) {
      setFilesOpen((prevFiles) => prevFiles.filter((f) => f !== fileName));
    } else {
      setFilesOpen((prevFiles) => [...prevFiles, fileName]);
    }
  };

  const onClickMarkFileAsAdminOnly = (
    file: FileObject,
    event?: React.MouseEvent,
  ) => {
    event?.stopPropagation();
    const updatedFiles = files.map((f) => {
      if (f.name === file.name) {
        return { ...f, adminOnly: !f.adminOnly };
      }
      return f;
    });
    setFiles(updatedFiles);
  };

  const onSetRecipientEmail = (file: FileObject, email: string) => {
    const updatedFiles = files.map((f) => {
      if (f.name === file.name) {
        return { ...f, recipientEmail: email };
      }
      return f;
    });
    setFiles(updatedFiles);
  };

  const onSetGLN = (file: FileObject, gln: string) => {
    const updatedFiles = files.map((f) => {
      if (f.name === file.name) {
        return { ...f, gln: gln };
      }
      return f;
    });
    setFiles(updatedFiles);
  };
  const onSetComment = (file: FileObject, comment: string) => {
    const updatedFiles = files.map((f) => {
      if (f.name === file.name) {
        return { ...f, comment: comment };
      }
      return f;
    });
    setFiles(updatedFiles);
  };

  const onSelectLookupResult = (item: any) => {
    setClientIdentifier(item?.clientIdentifier);
    setClientName(item?.companyName);
  };

  function fileToByteArray(file: File): Promise<number[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onload = () => {
        const buffer = reader.result as ArrayBuffer;
        const byteArray = Array.from(new Uint8Array(buffer));
        resolve(byteArray);
      };
      reader.onerror = (error) => reject(error);
    });
  }

  const onCompleteUpload = (result: any) => {
    if (result?.documentHandler?.createInvoiceUploadUnit?.success === true) {
      setSuccessMessage(t("documentHandler.documentUploaded"));
      setErrorMessage("");
      setFiles([]);
      setClientIdentifier("");
      setClientName("");
      setFilesOpen([]);
      setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
    } else {
      setErrorMessage(t("errors.somethingWentWrong"));
    }
  };
  const onErrorUpload = (errorMessage?: any) => {
    setErrorMessage(t("errors.somethingWentWrong") + errorMessage?.toString());
  };
  const { createInvoiceUploadUnit, error, loading } =
    useCreateInvoiceUploadUnit({
      onCompleted: onCompleteUpload,
      onError: onErrorUpload,
    });

  const onClickUploadFiles = async () => {
    const invoices: Array<{
      invoice: { content: any; contentType: string; fileName: string };
      invoiceAttachments?: Array<{
        content: string;
        contentType: string;
        fileName: string;
      }>;
      invoiceRecipientEmails?: Array<string>;
      invoiceRecipientGln?: string;
      serviceType: InvoiceServiceType;
      userMessage?: string;
    }> = [];

    for (const file of files) {
      const byteArray = await fileToByteArray(file.file);

      const fileItem = {
        invoice: {
          content: byteArray,
          contentType: "application/pdf",
          fileName: file.name,
        },
        invoiceRecipientEmails: file.recipientEmail
          ? [file.recipientEmail]
          : undefined,
        invoiceRecipientGln: file.gln,
        serviceType: file.adminOnly
          ? ("ADMINISTER_INVOICE" as InvoiceServiceType)
          : ("SELL_INVOICE" as InvoiceServiceType),
        userMessage: file.comment,
      };

      invoices.push(fileItem);
    }

    try {
      await createInvoiceUploadUnit({
        variables: {
          input: {
            clientIdentifier: clientIdentifier,
            invoices: invoices,
            disableAutoInterpretation: disableAutoInterpretation,
          },
        },
      });
    } catch (e) {
      setErrorMessage(t("errors.somethingWentWrong") + e?.toString());
    }
  };

  return (
    <div>
      <Row justify="center">
        <Subtitle>{t("documentHandler.uploadDocument")}</Subtitle>
      </Row>
      <HorizontalSpacer spacing={2} />
      <Row justify="center">
        <Col md={8}>
          <UploadFile
            text={t("documentHandler.dragAndDropOrClickToUploadDocument")}
            onDrop={handleUploadFile}
            multiple
          />
        </Col>
      </Row>
      <HorizontalSpacer spacing={1} />
      <Row justify="center">
        <div>
          <OutlinedButton
            title={t("documentHandler.orSelectFile")}
            onClick={handleButtonClick}
            medium
            color={theme.colors.primary}
          />
          <input
            type="file"
            accept="application/pdf"
            style={{ display: "none" }}
            ref={fileInputRef}
            onChange={handleFileSelect}
            multiple
          />
        </div>
      </Row>

      <HorizontalSpacer spacing={2} />
      <Body fontweight="700">{t("documentHandler.uploaded")}:</Body>
      <HorizontalSpacer spacing={1} />
      {files?.map((file) => (
        <FileContainer key={file.name}>
          <FileRow
            nogutter
            justify="between"
            align="center"
            onClick={() => onClickOpenFile(file.name)}
          >
            <TextButton
              title={file.name}
              onClick={() => {}}
              icon={<FileIconOne />}
            />
            <IconRow>
              <Tooltip
                text={
                  file.adminOnly
                    ? t("documentHandler.adminOnly")
                    : t("documentHandler.financeInvoice")
                }
              >
                <ToggleButton
                  isOn={file.adminOnly ?? false}
                  onToggle={(e) => onClickMarkFileAsAdminOnly(file, e)}
                  iconOn={<FileAdminIcon color="white" />}
                  iconOff={<FileAdminIcon color={theme.colors.primary} />}
                  size="small"
                />
              </Tooltip>
              <IconButton
                icon={<TrashIcon color="white" />}
                color={theme.colors.error}
                onClick={() => onClickRemoveFile(file)}
                size="small"
              />
              {checkFileOpen(file.name) ? (
                <ChevronButton
                  type="up"
                  onClick={() => onClickOpenFile(file.name)}
                />
              ) : (
                <ChevronButton
                  type="down"
                  onClick={() => onClickOpenFile(file.name)}
                />
              )}
            </IconRow>
          </FileRow>
          <SlideOutHeight isVisible={checkFileOpen(file.name)}>
            <div style={{ padding: "0px 16px" }}>
              <HorizontalSpacer spacing={1} />
              <Row nogutter justify="between" align="center">
                <TextField
                  label={t("documentHandler.recipientEmail")}
                  placeholder={t("common.email")}
                  onChange={(v) => onSetRecipientEmail(file, v)}
                />
                {/*
                  <TextField
                    label={t("documentHandler.emailDeliveryApproval")}
                    placeholder={t("common.email")}
                    onChange={(v) => onSetDeliveryApprovalEmail(file, v)}
                  />
                */}
                <TextField
                  label={t("documentHandler.glnIfEInvoice")}
                  placeholder={t("documentHandler.glnNumber")}
                  onChange={(v) => onSetGLN(file, v)}
                />
              </Row>
              <HorizontalSpacer spacing={1} />
              <MultilineTextField
                label={t("documentHandler.optionalComment")}
                placeholder={t("common.comment")}
                onChange={(v) => onSetComment(file, v)}
              />
            </div>
          </SlideOutHeight>
        </FileContainer>
      ))}

      <HorizontalSpacer spacing={2} />
      <Subheading>{t("documentHandler.selectIssuer")}</Subheading>
      <HorizontalSpacer spacing={1} />
      {clientIdentifier && (
        <div>
          <Body>
            {t("documentHandler.selectedCompany")}: {clientName},{" "}
            {clientIdentifier}
          </Body>
          <HorizontalSpacer spacing={1} />
        </div>
      )}
      <Lookup
        type="modal"
        onClickResult={(item) => onSelectLookupResult(item)}
        displayOnlyIssuer
      />

      {(errorMessage || error) && (
        <Alert
          type="error"
          title={errorMessage ?? t("errors.somethingWentWrong")}
          message={error?.toString()}
        />
      )}
      {successMessage && <Alert type="success" title={successMessage} />}

      <HorizontalSpacer spacing={0.25} />
      <Row justify="center">
        <Checkbox
          title={t("documentHandler.disableAutoInterpretation")}
          checked={disableAutoInterpretation}
          onChange={() =>
            setDisableAutoInterpretation(!disableAutoInterpretation)
          }
        />
      </Row>
      <HorizontalSpacer spacing={1.5} />
      <Row justify="center">
        <DefaultButton
          title={t("common.cancel")}
          icon={<CrossIcon color={theme.colors.text} />}
          onClick={onClose}
        />
        <VerticalSpacer spacing={2} />

        <ActionButton
          title={t("documentHandler.uploadDocument")}
          icon={<UploadIcon color={theme.colors.white} />}
          color={theme.colors.info}
          onClick={onClickUploadFiles}
          disabled={files?.length < 1 || !clientIdentifier}
          loading={loading}
        />
      </Row>
    </div>
  );
};

export default UploadDocumentModalContent;
